/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { RTEClient } from '@pickleballinc/rte-service-client';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { RTELog, RTEMatchInfo, RTEMatchLogs, RTEMatchupInfo, RTETiebreakerInfo } from '../../types/rte';

const ONE_HOUR = 60 * 60 * 1000;

// Events to track activity
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];

// Define the context and the type for the provider's value
interface RTEContextValue {
	logs: RTEMatchLogs[];
	matches: RTEMatchInfo[];
	matchups: RTEMatchupInfo[];
	tiebreakerUpdates: RTETiebreakerInfo[];
	updateOrAddMatch: (match: RTEMatchInfo, matchId: string | undefined) => void;
	updateOrAddMatchup: (matchup: RTEMatchupInfo) => void;
	subscribeToMatchups: (matchupUuids: string[]) => void;
	subscribeToTiebreaker: (tiebreakerUuid: string) => void;
}

// Create the context with default values
const RTEContext = createContext<RTEContextValue | undefined>(undefined);

// Custom hook to access the RTEContext
export const useRTE = <T extends { matchUuid: string } = RTEMatchInfo>() => {
	const context = useContext(RTEContext);
	if (!context) {
		throw new Error('useRTE must be used within a RTEProvider');
	}
	return context as unknown as Omit<typeof context, 'matches'> & { matches: T[] };
};

interface RTEProviderProps {
	matchUuids?: string[];
	matchupUuids?: string[];
	userToken: string;
	ipAddress: string;
	children: React.ReactNode;
	rteEnabled: boolean;
	environment: 'production' | 'development' | 'uat';
	withLogs?: boolean;
}

export const RTEProvider = ({
	matchUuids,
	matchupUuids,
	userToken,
	ipAddress,
	children,
	withLogs = false,
	rteEnabled,
	environment
}: RTEProviderProps) => {
	const [logs, setLogs] = useState<RTEMatchLogs[]>([]);
	// TODO: Consider exposing matches as Map<matchUuid, RTEMatchInfo> (or a parallel map) so consumers can resolve
	// live data by id in O(1) instead of matches.find(...) like the old matchesRTE[matchKey] pattern.
	const [matches, setMatches] = useState<RTEMatchInfo[]>([]);
	const [matchups, setMatchups] = useState<RTEMatchupInfo[]>([]);
	const [tiebreakerUpdates, setTiebreakerUpdates] = useState<RTETiebreakerInfo[]>([]);
	const clientRef = useRef<RTEClient | null>(null);

	// Function to add a log in the logs state (DB restriction: Same log will only come once through the RTE)
	const addLog = useCallback((messageLog: RTELog) => {
		const matchUuid = messageLog.match_uuid;

		setLogs((prevLogs) => {
			const matchIndexToUpdate = prevLogs.findIndex((match) => match.match_uuid === matchUuid);

			if (matchIndexToUpdate !== -1) {
				const logsCopy = structuredClone(prevLogs);

				const matchToUpdate = logsCopy[matchIndexToUpdate];

				if (matchToUpdate) {
					matchToUpdate.logs.push(structuredClone(messageLog));
					return logsCopy;
				}
			} else {
				return [
					{
						match_uuid: matchUuid,
						logs: [structuredClone(messageLog)]
					}
				];
			}

			return prevLogs;
		});
	}, []);

	// Function to add/update a match in the matches state
	const updateOrAddMatch = useCallback((messageMatch: RTEMatchInfo, matchId: string | undefined) => {
		const matchUuid = messageMatch.matchUuid || matchId;

		setMatches((prevMatches) => {
			const matchIndexToUpdate = prevMatches.findIndex((match) => match.matchUuid === matchUuid);

			if (matchIndexToUpdate !== -1) {
				const matchesCopy = [...prevMatches];
				const matchToUpdate = matchesCopy[matchIndexToUpdate];

				matchesCopy[matchIndexToUpdate] = { ...matchToUpdate, ...messageMatch };

				return matchesCopy;
			} else {
				return [...prevMatches, { ...messageMatch }];
			}
		});
	}, []);

	// Function to add/update a matchup in the matchups state
	const updateOrAddMatchup = useCallback((messageMatchup: RTEMatchupInfo) => {
		const matchupUuid = messageMatchup.uuid;

		setMatchups((prevMatchups) => {
			const matchupIndexToUpdate = prevMatchups.findIndex((matchup) => matchup.uuid === matchupUuid);

			if (matchupIndexToUpdate !== -1) {
				const matchupsCopy = [...prevMatchups];
				const matchupToUpdate = matchupsCopy[matchupIndexToUpdate];

				matchupsCopy[matchupIndexToUpdate] = { ...matchupToUpdate, ...messageMatchup };

				return matchupsCopy;
			} else {
				return [...prevMatchups, { ...messageMatchup }];
			}
		});
	}, []);

	const updateOrAddTiebreaker = useCallback((update: RTETiebreakerInfo) => {
		setTiebreakerUpdates((prev) => {
			const idx = prev.findIndex((t) => t.matchUuid === update.matchUuid);
			if (idx !== -1) {
				const copy = [...prev];
				copy[idx] = { ...copy[idx], ...update };
				return copy;
			}
			return [...prev, update];
		});
	}, []);

	const subscribeToTiebreaker = useCallback(
		(tiebreakerUuid: string) => {
			if (!tiebreakerUuid) return;
			const eventName = `tiebreaker_${tiebreakerUuid}`;
			clientRef.current?.addEventListener(eventName, (evt: any) => {
				try {
					const rawData = JSON.parse(evt.data);
					updateOrAddTiebreaker({ matchUuid: tiebreakerUuid, ...rawData });
				} catch (e) {
					console.error(`RTE - ${eventName} - Failed to parse tiebreaker data:`, evt.data);
				}
			});
		},
		[updateOrAddTiebreaker]
	);

	const subscribeToMatchups = useCallback((matchupUuids: string[]) => {
		// Subscribe to matchup-level events
		if (matchupUuids && matchupUuids.length > 0) {
			matchupUuids.forEach((matchupUuid) => {
				const matchupEventName = `matchup_${matchupUuid}`;

				clientRef.current?.addEventListener(matchupEventName, (evt: any) => {
					try {
						const matchupDataObj: RTEMatchupInfo = JSON.parse(evt.data);
						updateOrAddMatchup(matchupDataObj);
					} catch (e) {
						console.error(`RTE - ${matchupEventName} - Failed to parse matchup data. Matchup uuid: ${evt.id}. Matchup data: ${evt.data}`);
					}
				});
			});
		}
	}, []);

	useEffect(() => {
		if (!rteEnabled) {
			return;
		}

		// Initialize the RTEClient and subscribe to the "message" event
		const client = new RTEClient({
			endpoint: 'live-scoring',
			matches: matchUuids || [], // Subscribe to matches with uuids matchUuids, or all matches if matchUuids is undefined
			withLogs,
			environment,
			headers: {
				'PB-USER-IP': ipAddress,
				'PB-USER-TOKEN': userToken
			}
		});

		clientRef.current = client;

		// =========================
		// IDLE DETECTION LOGIC
		// =========================

		let idleTimeout: ReturnType<typeof setTimeout> | null = null;
		let intentionallyClosed = false;

		const closeClientForInactivity = () => {
			if (intentionallyClosed) return;

			intentionallyClosed = true;
			console.info('User inactive for 1h → closing RTE client');
			client.close();
		};

		const resetTimer = () => {
			if (intentionallyClosed) return;

			if (idleTimeout) {
				clearTimeout(idleTimeout);
			}

			idleTimeout = setTimeout(() => {
				closeClientForInactivity();
			}, ONE_HOUR);
		};

		ACTIVITY_EVENTS.forEach((event) => {
			window.addEventListener(event, resetTimer, { passive: true });
		});

		// Start timer initially
		resetTimer();

		// =========================
		// END OF IDLE DETECTION LOGIC
		// =========================

		// When specific matchUuids are provided, the SSE server sends named events
		// (event:<matchUuid>) which only fire on listeners registered for that exact name.
		// The generic 'message' listener only fires for events without an event: field.
		if (matchUuids && matchUuids.length > 0) {
			matchUuids.forEach((matchUuid) => {
				client.addEventListener(matchUuid, (evt: any) => {
					try {
						const matchDataObj = JSON.parse(evt.data);

						if (matchDataObj.log_index !== undefined) {
							if (withLogs) {
								addLog(matchDataObj as RTELog);
							}
						} else {
							updateOrAddMatch(matchDataObj as RTEMatchInfo, evt.id);
						}
					} catch {
						/* empty */
					}
				});

				if (withLogs) {
					const refLogsCustomMessage = `reflog_${matchUuid}`;

					client.addEventListener(refLogsCustomMessage, (evt: any) => {
						try {
							const logDataObj: RTELog = JSON.parse(evt.data);

							addLog(logDataObj);
						} catch {
							/* empty */
						}
					});
				}
			});
		} else {
			// All-matches mode: no specific UUIDs, server sends generic 'message' events
			client.addEventListener('message', (evt: any) => {
				try {
					const matchDataObj: RTEMatchInfo = JSON.parse(evt.data);

					updateOrAddMatch(matchDataObj, evt.id);
				} catch {
					/* empty */
				}
			});
		}

		// Subscribe to matchup-level events
		if (matchupUuids && matchupUuids.length > 0) {
			matchupUuids.forEach((matchupUuid) => {
				const matchupEventName = `matchup_${matchupUuid}`;

				client.addEventListener(matchupEventName, (evt: any) => {
					try {
						const matchupDataObj: RTEMatchupInfo = JSON.parse(evt.data);
						updateOrAddMatchup(matchupDataObj);
					} catch (e) {
						console.error(`RTE - ${matchupEventName} - Failed to parse matchup data. Matchup uuid: ${evt.id}. Matchup data: ${evt.data}`);
					}
				});
			});
		}

		// Handle any errors
		client.addEventListener('error', () => {
			// console.error('RTE - Stopping live match updates because of unexpected error:', error);

			if (intentionallyClosed) {
				return;
			}

			client.reset(matchUuids || []);
		});

		// Clean up the client on component unmount
		return () => {
			intentionallyClosed = true;

			if (idleTimeout) {
				clearTimeout(idleTimeout);
			}

			ACTIVITY_EVENTS.forEach((event) => {
				window.removeEventListener(event, resetTimer);
			});

			client.close();
			clientRef.current = null;
		};
	}, [addLog, updateOrAddMatch, ipAddress, userToken, withLogs, matchUuids, rteEnabled, environment]);

	return (
		<RTEContext.Provider
			value={{ logs, matches, matchups, tiebreakerUpdates, updateOrAddMatch, updateOrAddMatchup, subscribeToMatchups, subscribeToTiebreaker }}
		>
			{children}
		</RTEContext.Provider>
	);
};
